import { getAttemptScores } from '../../utils/examScores';
import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    getDoc, 
    documentId 
} from 'firebase/firestore';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer, 
    Cell 
} from 'recharts';
import { db } from '../../lib/firebase';
import { subscribeByIds } from '../../lib/subscribeByIds';
import Skeleton from '../../components/Skeleton';
import ToppersModal from '../../components/ToppersModal';
import ExamBrowser from './ExamBrowser';

const ExamList = ({ onStartExam, onReviewSubmission, userData, user }) => {
    // --- Existing State (UNCHANGED) ---
    const [loadError, setLoadError] = useState('');
    const [papers, setPapers] = useState([]);
    const [exams, setExams] = useState([]);
    const [attempts, setAttempts] = useState([]); 
    const [extraExams, setExtraExams] = useState({}); 
    const [isLoading, setIsLoading] = useState(true);
    const [expandedPaperId, setExpandedPaperId] = useState(null);
    const [viewingToppers, setViewingToppers] = useState(null);
    const [toppersExamTitle, setToppersExamTitle] = useState('');

    // --- 1. Fetch Logic (UNCHANGED) ---
    useEffect(() => {
        setIsLoading(true);
        setPapers([]); setExams([]); setAttempts([]); setExtraExams({}); setLoadError('');
        const ready = new Set();
        const done = name => { ready.add(name); if (ready.size === 3) setIsLoading(false); };
        const failed = name => () => { setLoadError('Some study data could not load. Check your connection and reload.'); done(name); };
        const unsubscribers = [
            subscribeByIds('papers', documentId(), userData?.assignedPapers, data => { setPapers(data); done('papers'); }, failed('papers')),
            subscribeByIds('exams', 'paperId', userData?.assignedPapers, data => { setExams(data); done('exams'); }, failed('exams'))
        ];
        if (user?.uid) {
            unsubscribers.push(onSnapshot(query(collection(db, 'attempts'), where('userId', '==', user.uid)), snap => {
                const data = snap.docs.map(d => ({ ...d.data(), id: d.id })).filter(a => a.submittedAt);
                data.sort((a, b) => (b.submittedAt?.toMillis() || 0) - (a.submittedAt?.toMillis() || 0));
                setAttempts(data); done('attempts');
            }, failed('attempts')));
        } else done('attempts');
        return () => unsubscribers.forEach(stop => stop());
    }, [userData?.assignedPapers, user?.uid]);

    // --- 2. Missing Exam Data (UNCHANGED) ---
    useEffect(() => {
        const fetchMissingExams = async () => {
            const missingIds = attempts
                .map(a => a.examId)
                .filter(id => !exams.find(e => e.id === id) && !extraExams[id]); 

            if (missingIds.length === 0) return;
            const uniqueMissingIds = [...new Set(missingIds)];

            for (const id of uniqueMissingIds) {
                try {
                    const snap = await getDoc(doc(db, 'exams', id));
                    if (snap.exists()) {
                        setExtraExams(prev => ({ ...prev, [id]: { id: snap.id, ...snap.data() } }));
                    }
                } catch (e) { console.error("Error:", id, e); }
            }
        };
        if (attempts.length > 0) fetchMissingExams();
    }, [attempts, exams, extraExams]);

    // --- Helper Functions (UNCHANGED) ---
    const togglePaper = (pId) => setExpandedPaperId(prev => (prev === pId ? null : pId));
    const getExamForAttempt = (eId) => exams.find(e => e.id === eId) || extraExams[eId];
    const handleViewToppers = (exam, displayTitle) => {
        setViewingToppers(exam.toppers);
        setToppersExamTitle(displayTitle || exam.title);
    };

    // --- 3. Cinematic Button Renderer (UPDATED FOR VERSIONING) ---
    const renderExamButton = (exam) => {
        // Use publishedMode if available, fallback to legacy mode
        const examMode = exam.publishedMode || exam.mode || 'on-demand';
        const status = exam.invigilationStatus || 'pending';

        const baseClass = "text-[10px] uppercase tracking-[0.2em] font-black py-3 px-6 rounded-none transition-all duration-500 shadow-xl";

        if (examMode === 'on-demand') {
            return (
                <button 
                    onClick={() => onStartExam(exam, 'on-demand')} 
                    className={`${baseClass} bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]`}
                >
                    Begin Session
                </button>
            );
        }

        if (examMode === 'invigilated') {
            if (status === 'pending') {
                return (
                    <button onClick={() => onStartExam(exam, 'waiting')} className={`${baseClass} border border-yellow-500/50 text-yellow-500 hover:bg-yellow-500 hover:text-black`}>
                        Enter Waiting Area
                    </button>
                );
            }
            if (status === 'active') {
                return (
                    <button onClick={() => onStartExam(exam, 'invigilated-start')} className={`${baseClass} bg-green-600 text-white animate-pulse`}>
                        Join Now
                    </button>
                );
            }
            return (
                <button className={`${baseClass} border border-white/10 text-gray-600 cursor-not-allowed`} disabled>
                    Terminated
                </button>
            );
        }
    };

    // --- 4. Performance Chart Logic (UPDATED FOR VERSIONING) ---
    const chartData = useMemo(() => {
        const data = attempts.filter(attempt => !getAttemptScores(attempt).pendingCount).map(attempt => {
            const exam = exams.find(e => e.id === attempt.examId) || extraExams[attempt.examId];
            const score = getAttemptScores(attempt).totalScore;
            const total = getAttemptScores(attempt).totalPossible;
            
            // Prefer published title for consistency
            const displayTitle = exam ? (exam.publishedTitle || exam.title) : 'Unknown';
            
            return {
                name: displayTitle,
                score: total > 0 ? Math.round((score / total) * 100) : 0,
                rawScore: score,
                totalMarks: total,
                date: attempt.submittedAt?.toDate().toLocaleDateString() || ''
            };
        });
        return data.reverse();
    }, [attempts, exams, extraExams]);

    return (
        <div className="space-y-20 animate-fade-in pb-20">
            {loadError && <p role="alert" className="p-4 border rounded-xl">{loadError}</p>}
            {viewingToppers && (
                <ToppersModal 
                    toppers={viewingToppers} 
                    examTitle={toppersExamTitle} 
                    onClose={() => setViewingToppers(null)} 
                />
            )}

            {/* --- SECTION 1: AVAILABLE EXAMS --- */}
            <section>
                <div className="flex items-center gap-6 mb-12">
                    <h2 className="text-3xl font-serif italic text-white shrink-0">Available Exams</h2>
                    <div className="h-[1px] w-full bg-gradient-to-r from-white/10 to-transparent" />
                </div>

                {isLoading ? (
                    <Skeleton type="row" count={3} isDark={true} />
                ) : (
                    <ExamBrowser papers={papers} exams={exams} attempts={attempts} renderExamButton={renderExamButton} onViewToppers={handleViewToppers} />
                )}
            </section>

            {/* --- SECTION 2: PERFORMANCE GRAPH --- */}
            {attempts.length > 0 && (
                <section className="bg-white/[0.02] border border-white/5 p-12 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8">
                        <div className="h-1 w-12 bg-gradient-to-r from-purple-600 to-pink-600" />
                    </div>
                    <h2 className="text-3xl font-serif italic mb-12">Performance Analytics</h2>
                    <div className="w-full h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff0a" />
                                <XAxis dataKey="name" tick={{fontSize: 9, fill: '#4b5563', fontWeight: 'bold'}} interval={0} angle={-45} textAnchor="end" height={70}/>
                                <YAxis tick={{fontSize: 10, fill: '#4b5563'}} domain={[0, 100]} axisLine={false} />
                                <Tooltip 
                                    cursor={{fill: 'rgba(255,255,255,0.03)'}} 
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            const d = payload[0].payload;
                                            return (
                                                <div className="bg-[#111] border border-white/10 p-4 shadow-2xl backdrop-blur-xl">
                                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-500 mb-2">{label}</p>
                                                    <p className="text-2xl font-black mb-1">{d.score}% <span className="text-[10px] text-gray-500">SCORE</span></p>
                                                    <p className="text-[9px] text-gray-400 uppercase tracking-widest">{d.rawScore} / {d.totalMarks} Points Secured</p>
                                                    <div className="mt-3 text-[8px] text-gray-600 tracking-tighter uppercase">{d.date}</div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }} 
                                />
                                <Bar dataKey="score" radius={[2, 2, 0, 0]} barSize={32}>
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill="url(#barGradient)" fillOpacity={0.8} />
                                    ))}
                                </Bar>
                                <defs>
                                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#9333ea" />
                                        <stop offset="100%" stopColor="#db2777" />
                                    </linearGradient>
                                </defs>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            )}

            {/* --- SECTION 3: PAST SUBMISSIONS --- */}
            {attempts.length > 0 && (
                <section>
                    <div className="flex items-center gap-6 mb-12">
                        <h2 className="text-3xl font-serif italic text-white shrink-0">Archived Submissions</h2>
                        <div className="h-[1px] w-full bg-gradient-to-r from-white/10 to-transparent" />
                    </div>
                    <div className="space-y-[1px] bg-white/5 border border-white/5 max-h-[600px] overflow-y-auto custom-scrollbar">
                        {attempts.map(attempt => {
                            const examInfo = getExamForAttempt(attempt.examId);
                            const paperName = examInfo && papers.find(p => p.id === examInfo.paperId)?.name;
                            const score = getAttemptScores(attempt).totalScore;
                            const total = getAttemptScores(attempt).totalPossible;
                            
                            // Prefer published title for historical records as well
                            const displayTitle = examInfo ? (examInfo.publishedTitle || examInfo.title) : "Archived Exam";

                            return (
                                <div key={attempt.id} className="p-10 flex flex-col md:flex-row justify-between items-center hover:bg-white/[0.02] transition-colors group">
                                    <div className="mb-6 md:mb-0">
                                        <p className="text-[9px] text-purple-500 font-bold uppercase tracking-[0.3em] mb-2">{paperName || 'Academy Exam'}</p>
                                        <h4 className="text-xl font-bold tracking-tight uppercase group-hover:translate-x-2 transition-transform duration-500">
                                            {displayTitle}
                                        </h4>
                                        <p className="text-[10px] text-gray-600 uppercase tracking-widest mt-2 font-bold">
                                            Authenticated: {attempt.submittedAt?.toDate().toLocaleString() || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-12">
                                        <div className="text-right">
                                            <p className="text-2xl font-black text-white">{score} <span className="text-gray-600 text-sm font-light">/ {total}</span></p>
                                            <p className="text-[8px] text-gray-500 uppercase tracking-[0.4em] font-black">{getAttemptScores(attempt).pendingCount ? 'Provisional — awaiting evaluation review' : 'Performance Index'}</p>
                                        </div>
                                        {examInfo ? (
                                            <button 
                                                onClick={() => onReviewSubmission(examInfo, attempt)} 
                                                className="text-[9px] font-black uppercase tracking-[0.3em] border border-white/20 px-8 py-3 hover:bg-white hover:text-black transition-all duration-500"
                                            >
                                                Audit Feedback
                                            </button>
                                        ) : (
                                            <span className="text-[9px] text-gray-700 uppercase tracking-widest">Historical Data Only</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ExamList;